import {
  FunctionComponent,
  CSSProperties,
  useEffect,
  useContext,
  useRef,
  useState,
  useMemo,
} from 'react';

import {
  Input,
  AlienIcon,
  SearchIcon,
  IDropdownItem,
  Dropdown,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import type { IWorker } from 'types';
import { getRandomNumber } from 'utils';

import { AuthContext } from '../../auth';
import { useWorkers } from './useWorkers';
import s from './WorkerSuggest.module.scss';

export interface IWorkerSuggestProps {
  className?: string;
  style?: CSSProperties;
  value: Omit<IWorker, 'creator'> | null;
  onChange: (worker: Omit<IWorker, 'creator'> | null) => void;
}

const INPUT_NAME = 'worker-suggest';

const ICON_PROPS = {
  color: 'var(--text-secondary)',
  style: { cursor: 'pointer' },
};

export const WorkerSuggest: FunctionComponent<IWorkerSuggestProps> = ({
  className = '',
  style,
  onChange,
  value,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { inputValue, setInputValue, workers } = useWorkers(value);

  const inputRef = useRef<HTMLInputElement>(null);

  const [visible, setVisibility] = useState(false);

  const shouldShowSuggests = visible && workers.length > 0;

  const items = useMemo<Array<IDropdownItem<number, Omit<IWorker, 'creator'>>>>(
    () =>
      workers.map((worker) => ({
        value: worker.id,
        original: worker,
      })),
    [workers]
  );

  useEffect(() => {
    if (!value) {
      setInputValue('');
    }

    const { last_name, first_name } = value || {};

    if (last_name && first_name) {
      setInputValue(`${last_name} ${first_name}`);
    }
  }, [value, setInputValue, onChange]);

  useEffect(() => {
    if (!inputValue) {
      onChange(null);
    }
  }, [inputValue, onChange]);

  return (
    <div style={style} className={cn(s.root, className)}>
      <div>
        <Input
          variant="secondary"
          onFocus={() => {
            setVisibility(true);
          }}
          onBlur={() => {
            setVisibility(false);
          }}
          ref={inputRef}
          name={`${INPUT_NAME}-${getRandomNumber()}`}
          value={inputValue}
          onChange={setInputValue}
          onBackspace={() => {
            if (value) {
              return onChange(null);
            }
          }}
          leftContent={<SearchIcon color="primary" />}
          {...(inputValue ? { iconProps: ICON_PROPS } : {})}
          placeholder={t('workers.suggest.placeholder')}
          hideBottomBorder={shouldShowSuggests}
          disabled={!permissions?.has('worker_manage')}
        />
        {shouldShowSuggests && (
          <Dropdown
            items={items}
            value={value?.id}
            onChange={(id) =>
              onChange(workers.find((worker) => worker.id === id) ?? null)
            }
            close={() => inputRef.current?.blur()}
            renderItem={(item) => (
              <div className={s['user-suggest']}>
                <AlienIcon color="link-info" className={s['user-avatar']} />
                <span
                  className={s['user-name']}
                >{`${item.original?.last_name} ${item.original?.first_name}`}</span>
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
};
